"use strict";

import { LocalLoadFile } from 'api/basicApi';
import GaijinToolPanel from 'components/gaijinToolPanel';
import { _I_, defineSimpleTerm } from 'components/locs';
import { staticConfigs } from 'components/setup/configSources';
import { SourceListener } from 'generics/dataBound';
import { IconFontAwesome } from 'generics/icons';
import React from 'react';
import ReactGA from 'react-ga';

LogStream.enable("CONFIG");
const loggerConfig = LogStream("CONFIG")


function loadSiteSettings() {
  return LocalLoadFile(
    "config/settings.json"
  ).catch((error) => Promise.reject("settings"));
}


function loadDevSiteSettings() {
  return LocalLoadFile(
    "config/dev_settings.json"
  ).catch((error) => ({}));  // It's normal if there no such file
}


function setupSiteSettings() {
  return Promise.all([
    loadSiteSettings(),
    loadDevSiteSettings(),

  ]).then(([mainSiteSettings, devSiteSettings]) => {
    const siteSettings = Object.assign({}, mainSiteSettings, devSiteSettings);
    if (!siteSettings.ssoUrl)
      siteSettings.devMode = true;

    staticConfigs.update({ siteSettings });

    loggerConfig.info(`siteSettings loaded successfully${siteSettings.devMode ? ", devmode set" : ""}`);
    return !!siteSettings.devMode;
  });
}


function loadCircuits() {
  return LocalLoadFile(
    "config/circuits.json"
  ).catch((error) => Promise.reject("circuits"));
}


function loadDevCircuits() {
  return LocalLoadFile(
    "config/dev_circuits.json"
  ).catch((error) => ({}));  // It's normal if there no such file
}


function setupCircuits() {
  return Promise.all([
    loadCircuits(),
    loadDevCircuits(),

  ]).then(([mainCircuits, devCircuits,]) => {
    const circuits = Object.assign({}, mainCircuits, devCircuits)

    let usedCircuitName = utils_api.getStored(["MarketApp", "LRU", "circuit"], "test");

    if (!circuits[usedCircuitName]) {
      usedCircuitName = "test";
      if (!circuits[usedCircuitName]) {
        usedCircuitName = Object.keys(circuits)[0];
      }

      utils_api.setStored(["MarketApp", "LRU", "circuit"], usedCircuitName);
    }

    const usedCircuit = circuits[usedCircuitName];

    SettingsInjections._circuit = usedCircuit;
    SettingsInjections._circuitName = usedCircuitName;

    staticConfigs.update({ circuits, usedCircuit, usedCircuitName });
    loggerConfig.info(`circuits loaded successfully: running on ${usedCircuitName}`);
    return usedCircuitName;
  });
}

function setupAnalytics() {
  loggerConfig.info(`google analytics initialize`);
  ReactGA.initialize([{
    trackingId: 'UA-40910063-49'
  }]);
}

function loadStaticConfigs() {
  return Promise.all([
    setupSiteSettings(),
    setupCircuits(),
    setupAnalytics()

  ]).then(([devMode, usedCircuitName, usedLocale]) => {
    loggerConfig.info(`static configs initialization finished`);
  });
}


function StaticConfigError({ error }) {
  return <GaijinToolPanel type="danger"
    caption={_I_("StaticConfigLoader.caption.error")} >
    <div className="text-center">
      <div className="alert alert-danger">
        <h3><IconFontAwesome type="exclamation-triangle" size="3x" fixedWidth /></h3>
        {_I_(`StaticConfigLoader.error.${error}`)}
      </div>
    </div>
  </GaijinToolPanel>;
}


function StaticConfigWaitbox({ }) {
  return <GaijinToolPanel caption={_I_("StaticConfigLoader.caption.init")} >
    <div className="text-center">
      <h3>{_I_("StaticConfigLoader.text.init")}</h3>
      <IconFontAwesome type="spinner" size="3x" pulse fixedWidth />
    </div>
  </GaijinToolPanel>;
}


export class StaticConfigLoader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      stage: "pending",
    };
  }


  componentDidMount() {
    loadStaticConfigs().then(
      () => { this.setState({ stage: "done" }) },
      (error) => {
        loggerConfig.warning("StaticConfigLoader error", error);
        this.setState({ stage: error });
      });
  }


  render() {
    const { stage } = this.state;
    switch (stage) {
      case "done":
        return (<SourceListener conf={staticConfigs} >{
          ({ conf }) => (this.props.children(conf))
        }</SourceListener>);

      case "pending":
        return (<StaticConfigWaitbox />);

      default:
        return (<StaticConfigError error={stage} />);
    }
  }
}


defineSimpleTerm("StaticConfigLoader.caption.error", "Initialization error", {
  "ru_RU": "Ошибка инициализации",
  "cs_CZ": "Initialization error",
  "fr_FR": "Erreur d'initialisation",
  "de_DE": "Initialisierungsfehler",
  "it_IT": "Errore Inizializzazione",
  "ko_KR": "실행 오류",
  "pl_PL": "Błąd inicjalizacji",
  "pt_PT": "Initialization error",
  "es_ES": "Error de inicialización",
  "zh_CN": "初始化错误",
  "zh_TW": "初始化錯誤"
});


defineSimpleTerm("StaticConfigLoader.caption.init", "Initialization", {
  "ru_RU": "Инициализация",
  "cs_CZ": "Initialization",
  "fr_FR": "Initialisation",
  "de_DE": "Initialisierung",
  "it_IT": "Inizializzazione",
  "ko_KR": "실행중",
  "pl_PL": "Inicjalizacja",
  "pt_PT": "Initialization",
  "es_ES": "Inicialización",
  "zh_CN": "初始化",
  "zh_TW": "初始化"
});


defineSimpleTerm("StaticConfigLoader.text.init", "Initializing application...", {
  "ru_RU": "Инициализация приложения...",
  "cs_CZ": "Initializing application...",
  "fr_FR": "Initialisation de l'application ...",
  "de_DE": "Initialisierungantrag...",
  "it_IT": "Inizializzazione applicazione...",
  "ko_KR": "앱 실행중",
  "pl_PL": "Inicjalizowanie aplikacji...",
  "pt_PT": "Initializing application...",
  "es_ES": "Inicializando la aplicación...",
  "zh_CN": "正在初始化应用程序…",
  "zh_TW": "正在初始化應用程序..."
});


defineSimpleTerm("StaticConfigLoader.error.settings", "Unable to load site settings", {
  "ru_RU": "Не удалось загрузить настройки приложения",
  "cs_CZ": "Unable to load site settings",
  "fr_FR": "Impossible de charger les paramètres du site",
  "de_DE": "Nicht möglich die Seiteneinstellungen zu laden",
  "it_IT": "Impossibile caricare le impostazioni del sito",
  "ko_KR": "웹페이지 설정을 불러올 수 없습니다",
  "pl_PL": "Nie udało się wczytać ustawień strony",
  "pt_PT": "Unable to load site settings",
  "es_ES": "No se puede cargar la configuración del sitio",
  "zh_CN": "无法加载网站设置",
  "zh_TW": "無法載入網站設置"
});


defineSimpleTerm("StaticConfigLoader.error.circuits", "Unable to load services config", {
  "ru_RU": "Не удалось загрузить настройки контура",
  "cs_CZ": "Unable to load services config",
  "fr_FR": "Impossible de charger la configuration des services",
  "de_DE": "Nicht möglich die Dienstleistungseinstellungen zu laden",
  "it_IT": "Impossibile caricare le configurazioni dei servizi",
  "ko_KR": "서비스 설정 파일을 불러올 수 없습니다",
  "pl_PL": "Nie udało się wczytać konfiguracji serwisów",
  "pt_PT": "Unable to load services config",
  "es_ES": "No se puede cargar la configuración de servicios",
  "zh_CN": "无法加载服务配置",
  "zh_TW": "無法載入服務配置"
});

